import { useEffect, useRef, useState } from "react";

/**
 * `value` once it has not changed for `delay` ms (search as you type), and never sooner than
 * `interval` ms after the previous one, mount included: the API allows one search per second per
 * user (RATE_LIMIT_SEARCH), so typing never runs into a 429.
 */
export function useThrottledValue<T>(
	value: T,
	{ delay = 300, interval = 1000 }: { delay?: number; interval?: number } = {},
): T {
	const [throttled, setThrottled] = useState(value);
	const current = useRef(value);
	const last = useRef(Date.now());
	useEffect(() => {
		// typed back to the value already out: nothing to send (the pending one was cancelled)
		if (Object.is(value, current.current)) return;
		const wait = Math.max(delay, last.current + interval - Date.now());
		const timer = setTimeout(() => {
			last.current = Date.now();
			current.current = value;
			setThrottled(value);
		}, wait);
		return () => clearTimeout(timer);
	}, [value, delay, interval]);
	return throttled;
}
